import { userPermissions } from '@/common/constants'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import { statuses } from '@/returns/constants'
import { refundMethods } from '@/returns/pages/TransactionDetailPage/constants'
import { canDoPartialValidation, canPutRefundOnHold } from '@/returns/pages/TransactionDetailPage/helpers'

const useTransactionPermissions = transaction => {
  const { hasPermission } = useUserPermissions()

  const storeId = transaction?.store_id
  const permissions = transaction?.permissions
  const availableStatus = transaction?.available_status
  const isCancelled = transaction?.status === statuses.cancelled

  const allowsEditTransactionTotal = hasPermission(userPermissions.editTransactionTotal, storeId)
  const allowsEditReturnStatus = hasPermission(userPermissions.editReturnStatus, storeId)
  const allowsEditExchangeOrders = hasPermission(userPermissions.editExchangeOrders, storeId)
  const allowsEditIssueRefund = hasPermission(userPermissions.editIssueRefund, storeId)
  const allowsEditRefundMethod = hasPermission(userPermissions.editRefundMethod, storeId)
  const allowsEditStoreCreditGiftCard = hasPermission(userPermissions.editStoreCreditGiftCard, storeId)
  const allowsEditCustomerData = hasPermission(userPermissions.editCustomerData, storeId)
  const allowsEditReturnLabels = hasPermission(userPermissions.editReturnLabels, storeId)

  const allowIssueOpmOnCancel = transaction?.refund_info?.adjustment_refund_methods?.some(
    method => method.name === refundMethods.originalPaymentMethod && method.enabled
  )

  return {
    allowsEditTransactionTotal,
    allowsEditReturnStatus,
    allowsEditExchangeOrders,
    allowsEditIssueRefund,
    allowsEditRefundMethod,
    allowsEditStoreCreditGiftCard,
    allowsEditCustomerData,
    allowsEditReturnLabels,
    isDisabledIssueRefund: isCancelled || !allowsEditIssueRefund || !transaction?.refund_info?.available_refund_type,
    isAcceptOrRejectDisabled:
      !allowsEditReturnStatus ||
      !availableStatus?.includes(statuses.accepted) ||
      !availableStatus?.includes(statuses.rejected),
    isDisabledForOnHoldAndPendingStatus: [statuses.onHold, statuses.pending].includes(transaction?.status),
    allowRevertCancelStatus: permissions?.allows_uncancel,
    allowPutRefundOnHold: canPutRefundOnHold({
      hasIssueRefundPermission: allowsEditIssueRefund,
      allowsPutRefundOnHold: permissions?.allows_put_refund_on_hold,
    }),
    allowMarkAsIssued: permissions?.allows_issue_manual_refund && allowsEditIssueRefund,
    allowMarkAsPaid: permissions?.allows_issue_manual_payment && allowsEditIssueRefund,
    allowCancelStoreCredit: permissions?.allows_cancel_store_credit && allowsEditStoreCreditGiftCard,
    allowCancelGiftCard: permissions?.allows_cancel_gift_card && allowsEditStoreCreditGiftCard,
    allowPartialValidation: canDoPartialValidation(availableStatus, allowsEditReturnStatus),
    allowCreateExchangeOrder: permissions?.allows_exchange_order_processing_forced && allowsEditExchangeOrders,
    allowEditCustomer: permissions?.allows_update_customer && allowsEditCustomerData,
    allowAddTrackingNumber: permissions?.allows_mark_as_shipped && allowsEditReturnLabels,
    allowChangeDeliveryMethod: permissions?.allows_update_delivery_method && allowsEditReturnLabels,
    allowSendInstructions: permissions?.allows_return_instructions_request && allowsEditReturnLabels,
    allowIssueOpmOnCancel,
  }
}

export default useTransactionPermissions
